import { useState, useMemo, useEffect } from "react";
import { Cloudinary } from "@cloudinary/url-gen";
import Select from 'react-select';
import countryList from 'react-select-country-list';
import './ContactForm.css';

// Initialize Cloudinary instance
const cld = new Cloudinary({
  cloud: {
    cloudName: 'di0r4dirk'
  }
});

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telephone: "",
    country: "",
    reason: "",
    message: "",
    termsAccepted: false,
  });

  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHeroLoaded, setIsHeroLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1000
  );

  // Get country options using react-select-country-list
  const options = useMemo(() => countryList().getData(), []);

  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get optimized hero image URL from Cloudinary
  const getHeroImageUrl = () => {
    const publicId = 'mail-man_w4oci9';
    
    let targetWidth = 1000; // Default size as requested
    
    if (windowWidth <= 768) {
      targetWidth = 768; // Mobile
    } else if (windowWidth <= 1000) {
      targetWidth = 1000; // Default
    }
    
    const heroImg = cld.image(publicId)
      .format('auto')
      .quality('auto:good')
      .resize(`w_${targetWidth}`);
    
    return heroImg.toURL();
  };

  // Preload the hero image
  useEffect(() => {
    const preloadHeroImage = () => {
      const img = new Image();
      img.src = getHeroImageUrl();
      img.onload = () => setIsHeroLoaded(true);
      img.onerror = () => {
        console.error('Failed to load hero image');
        setIsHeroLoaded(true);
      };
    };

    preloadHeroImage();
  }, [windowWidth]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    setIsLoading(true);

    if (!formData.termsAccepted) {
      setError("You must accept the terms and conditions.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://impactstoriesfund.org/backend/public/api/contact",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccess("Your message has been sent successfully!");
        setFormData({
          name: "",
          email: "",
          telephone: "",
          country: "",
          reason: "",
          message: "",
          termsAccepted: false,
        });
      } else {
        setError(result.message || "Something went wrong!");
      }
    } catch (err) {
      setError("Failed to send message, please try again later.");
    }

    setIsLoading(false);
  };

  return (
    <div className="">
      {/* Hero Section with Cloudinary Optimization */}
      <div 
        className={`contact-hero-section ${isHeroLoaded ? 'loaded' : 'loading'}`}
        style={{
          backgroundImage: `url(${getHeroImageUrl()})`,
          backgroundPosition: 'bottom',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="overlay">
          <h1 className="hero-title">
            Get In TOUCH
          </h1>
        </div>
      </div>
      
      <div className="contact-form-container"> 
        <div className="contact-form-box">
          <div className="container contact-form">
            <form onSubmit={handleSubmit} className="p-4 bg-light rounded shadow">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    placeholder="Name*"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder="Email*"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <input
                    type="text"
                    name="telephone"
                    className="form-control"
                    placeholder="Telephone number"
                    value={formData.telephone}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <Select
                    options={options}
                    name="country"
                    classNamePrefix="react-select"
                    value={options.find(option => option.label === formData.country) || null}
                    onChange={selectedOption =>
                      setFormData({ ...formData, country: selectedOption ? selectedOption.label : "" })
                    }
                    placeholder="Select country"
                    isClearable
                  />
                </div>
                <div className="col-md-12 mb-3">
                  <select
                    name="reason"
                    className="form-control"
                    value={formData.reason}
                    onChange={handleChange}
                  >
                    <option value="">Select a reason for contact</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Support Request">Support Request</option>
                    <option value="Feedback">Feedback</option>
                  </select>
                </div>
                <div className="col-md-12 mb-3">
                  <textarea
                    name="message"
                    className="form-control"
                    rows="5"
                    placeholder="Message*"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
                <div className="col-md-12 mb-3 form-check">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    className="form-check-input"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    required
                  />
                  <label className="form-check-label">
                    I accept the{" "}
                    <a href="https://www.termsfeed.com/live/e28e7670-cba0-4a4d-93c3-fe591fd8ea47" target="_blank" rel="noopener noreferrer">
                      Terms and Conditions
                    </a>
                    *
                  </label>
                </div>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
              <br />
              {success && <p className="alert alert-success">{success}</p>}
              {error && <p className="alert alert-danger">{error}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;